import { computed, defineComponent, onMounted, ref, watch, } from '@vue/composition-api';
import { GenericAGTable } from '@/components/Table/AGTable/AGTable.vue';
import { ObjectStatus, getObjectsList, } from '@/api/v2/requests/objects';
import { ObjectStatus as ObjectInternalStatus } from '@/api/requests/objects';
import { useBrand } from '@/composables';
import { useObjectResourcesStore } from '@/store/object-resources';
import { DummyFilter } from '@/components/Table/dummy-filter';
import { useResourcesTableQuery } from '@/views/Brand/Applications/Components/Resources/_composables/use-resources-table-query';
import SelectionCell from '@/views/Brand/Applications/Components/Resources/_cell-renderers/SelectionCell.vue';
import PreviewCell from '@/views/Brand/Applications/Components/Resources/_cell-renderers/PreviewCell.vue';
import FloatingTextFilter from '@/components/Table/AGTable/_components/FloatingTextFilter.vue';
import ObjectPreview from '@/views/Brand/Objects/ObjectPreview.vue';
import Pagination from '@/components/Pagination/Pagination.vue';
import PaginationSelect from '@/components/Inputs/PaginationSelect/PaginationSelect.vue';
export default defineComponent({
    components: {
        AgTable: GenericAGTable(),
        ObjectPreview,
        Pagination,
        PaginationSelect,
        // eslint-disable-next-line vue/no-unused-components
        SelectionCell,
        // eslint-disable-next-line vue/no-unused-components
        PreviewCell,
        // eslint-disable-next-line vue/no-unused-components
        FloatingTextFilter,
    },
    props: {
        reportId: {
            type: String,
        },
        query: {
            type: Object,
            default: () => ({}),
        },
        containerSelector: {
            type: String,
            required: true,
        },
    },
    setup(props, ctx) {
        const { brand } = useBrand();
        const { isObjectSelected, onObjectSelection } = useObjectResourcesStore();
        const gridApi = ref(null);
        const isLoading = ref(false);
        const rows = ref([]);
        const totalCount = ref(0);
        const previewObjectId = ref(null);
        const { page, perPage, query: tableQuery, handleFilterChange: handleQueryFilterChange, handleSort, resetSort, } = useResourcesTableQuery({
            defaultPerPage: 25,
            filterParams: {
                internalNumber: 'internalNumber',
                placeName: 'placeName',
                address: 'address',
                costCenterName: 'costCenterName',
            },
            fieldMap: {
                costCenter: 'costCenterName',
                placeName: 'placeNameFromPlaces',
            },
            staticParams: {
                status: ObjectStatus.ACTIVE,
                internalStatus: ObjectInternalStatus.ACTIVE,
            },
            perPageKey: 'perPage',
            sortKey: 'sort',
        });
        const apiQuery = computed(() => ({
            ...tableQuery.value,
            ...(brand.value?.id && { brandId: brand.value.id }),
            ...props.query,
            ...(props.reportId && { relatedReportId: props.reportId }),
        }));
        const totalPages = computed(() => Math.ceil(totalCount.value / perPage.value));
        const columns = computed(() => [
            {
                headerName: '',
                field: 'id',
                cellRenderer: 'SelectionCell',
                cellRendererParams: {
                    isSelected: (data) => isObjectSelected(data),
                    onToggle: (data) => {
                        onObjectSelection([data.id], [data]);
                        gridApi.value?.refreshCells({ columns: ['id'] });
                    },
                },
                width: 60,
                minWidth: 60,
                maxWidth: 60,
                sortable: false,
                filter: false,
                suppressMovable: true,
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            },
            {
                field: 'internalNumber',
                headerName: '#',
                sortable: true,
                width: 140,
                minWidth: 120,
                flex: 0,
                filter: DummyFilter,
                floatingFilter: true,
                floatingFilterComponent: 'FloatingTextFilter',
                suppressFloatingFilterButton: true,
            },
            {
                colId: 'placeName',
                headerName: ctx.root.$t('object-list-view.place').toString(),
                valueGetter: (params) => {
                    const data = params.data;
                    if (!data)
                        return '-';
                    if (data.placeNames) {
                        return data.placeNames;
                    }
                    return data.place?.name || '-';
                },
                flex: 1,
                minWidth: 200,
                sortable: true,
                filter: DummyFilter,
                floatingFilter: true,
                floatingFilterComponent: 'FloatingTextFilter',
                suppressFloatingFilterButton: true,
            },
            {
                field: 'address',
                headerName: ctx.root.$t('object-list-view.address').toString(),
                valueGetter: (params) => {
                    const data = params.data;
                    if (!data)
                        return '-';
                    const parts = [];
                    if (data.city)
                        parts.push(data.city);
                    if (data.address)
                        parts.push(data.address);
                    return parts.join(', ') || '-';
                },
                flex: 1,
                minWidth: 150,
                sortable: true,
                filter: DummyFilter,
                floatingFilter: true,
                floatingFilterComponent: 'FloatingTextFilter',
                suppressFloatingFilterButton: true,
            },
            {
                field: 'costCenter',
                headerName: ctx.root.$t('object-list-view.cost-center').toString(),
                valueGetter: (params) => {
                    const data = params.data;
                    if (!data)
                        return '-';
                    const costs = data.currentCentralCosts;
                    if (costs && costs.length > 0) {
                        return costs[0]?.centralCost?.name || '-';
                    }
                    return '-';
                },
                flex: 1,
                minWidth: 150,
                sortable: true,
                filter: DummyFilter,
                floatingFilter: true,
                floatingFilterComponent: 'FloatingTextFilter',
                suppressFloatingFilterButton: true,
            },
            {
                headerName: '',
                field: 'preview',
                cellRenderer: 'PreviewCell',
                cellRendererParams: {
                    isExpanded: (data) => previewObjectId.value === data.id,
                    onTogglePreview: (data) => {
                        previewObjectId.value =
                            previewObjectId.value === data.id ? null : data.id;
                        gridApi.value?.refreshCells({ columns: ['preview'], force: true });
                    },
                },
                width: 60,
                minWidth: 60,
                maxWidth: 60,
                sortable: false,
                filter: false,
                suppressMovable: true,
            },
        ]);
        let fetchId = 0;
        const fetchItems = async () => {
            const currentFetchId = ++fetchId;
            isLoading.value = true;
            gridApi.value?.showLoadingOverlay();
            try {
                const result = await getObjectsList(apiQuery.value);
                if (currentFetchId !== fetchId)
                    return;
                rows.value = result.nodes;
                totalCount.value = result.totalCount;
            }
            catch (err) {
                console.error('Error fetching objects:', err);
            }
            finally {
                if (currentFetchId === fetchId) {
                    isLoading.value = false;
                    gridApi.value?.hideOverlay();
                }
            }
        };
        const changePage = (newPage) => {
            page.value = newPage;
        };
        const changePerPage = (newPerPage) => {
            perPage.value = newPerPage;
            page.value = 1;
        };
        const handleRowClick = (data) => {
            onObjectSelection([data.id], [data]);
            gridApi.value?.refreshCells({ columns: ['id'] });
        };
        const handleFilterChange = (filterModel) => {
            handleQueryFilterChange(filterModel);
        };
        const handleSortChange = (field, sortDir) => {
            handleSort(field, sortDir);
        };
        const handleResetSort = () => {
            resetSort();
        };
        const onGridReady = (event) => {
            gridApi.value = event.api;
        };
        const closePreview = () => {
            previewObjectId.value = null;
            gridApi.value?.refreshCells({ columns: ['preview'], force: true });
        };
        onMounted(fetchItems);
        watch(apiQuery, fetchItems, { deep: true });
        return {
            columns,
            rows,
            totalCount,
            totalPages,
            page,
            perPage,
            previewObjectId,
            handleRowClick,
            handleFilterChange,
            handleSortChange,
            handleResetSort,
            onGridReady,
            changePage,
            changePerPage,
            closePreview,
        };
    },
});
